/**
 * O conteúdo da caixa "Copiar semana anterior".
 *
 * Papel: pede uma data dentro da semana que vai receber a cópia, escreve logo
 * abaixo quais duas semanas a cópia envolve, oferece as férias e as diárias que
 * tocam a semana de origem para serem copiadas também, e, em "Continuar",
 * pergunta uma última vez antes de gravar — a mesma pergunta da origem, com o
 * que vai e o que não vai junto.
 *
 * Como usar: quem monta passa o mês mostrado e o primeiro dia da semana da
 * barra, e deverá montar este componente só com a caixa aberta, para cada
 * abertura começar da data padrão: hoje, quando a grade mostra o mês corrente,
 * ou o dia 1 do mês mostrado. `onConcluida` fecha a caixa, depois da cópia ou
 * em "Cancelar".
 *
 * As férias e as diárias começam desmarcadas. A cópia dos dias substitui a
 * semana de destino inteira — o dia de origem sem registro apaga o de destino —,
 * e os períodos marcados viram registros novos deslocados 7 dias, com os
 * originais no lugar.
 */
import { dataSchema } from '@concierge/shared';
import { useState } from 'react';

import { ErroDaApi } from '@/shared/api';
import { hojeEmIso } from '@/shared/lib/vencimento';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Button } from '@/shared/ui/button';
import { CampoMascarado } from '@/shared/ui/campo-mascarado';
import { Checkbox } from '@/shared/ui/checkbox';
import { DialogFooter } from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import { nomeNaEscala } from '../escala';
import {
  type MesMostrado,
  type PrimeiroDia,
  diaEMes,
  pontasDoMes,
  semanasDaCopia,
} from '../grade';
import {
  useAusenciasDoPeriodo,
  useCopiarSemana,
  useVisitasDoPeriodo,
} from '../hooks/usar-calendario';

interface CaixaCopiarSemanaProps {
  mostrado: MesMostrado;
  primeiroDia: PrimeiroDia;
  residenciaId: string;
  /** Chamado ao fechar a caixa, com a cópia feita ou cancelada. */
  onConcluida: () => void;
}

/**
 * A data em que a caixa abre.
 *
 * @param mostrado - O mês que a grade está mostrando.
 * @returns Hoje, quando a grade mostra o mês corrente; o dia 1 do mês, nos
 *   outros.
 */
function dataPadrao(mostrado: MesMostrado): string {
  const hoje = hojeEmIso();
  const doMes = pontasDoMes(mostrado);
  return hoje >= doMes.de && hoje <= doMes.ate ? hoje : doMes.de;
}

/** Um período como a caixa o escreve: o nome e as duas pontas em `DD/MM`. */
function comoSeLe(nome: string, periodo: { inicio: string; fim: string }): string {
  return `${nome} (${diaEMes(periodo.inicio)} a ${diaEMes(periodo.fim)})`;
}

/** O título de cada grupo de períodos marcáveis. */
const TITULO_DO_GRUPO = 'mb-1 text-[11.5px] text-texto-fraco';

/** Cada período marcável, com a caixa de marcar à esquerda. */
const ITEM_MARCAVEL = 'mb-1.5 flex cursor-pointer items-start gap-2 text-xs text-texto-medio';

/**
 * A caixa da cópia.
 *
 * @param mostrado - O mês da grade, de onde sai a data padrão.
 * @param primeiroDia - O dia em que a semana começa, o mesmo da grade.
 * @param residenciaId - A casa do calendário aberto.
 * @param onConcluida - Fecha a caixa.
 * @returns O formulário da caixa e, depois de "Continuar", a confirmação.
 */
export function CaixaCopiarSemana({
  mostrado,
  primeiroDia,
  residenciaId,
  onConcluida,
}: CaixaCopiarSemanaProps) {
  const [data, setData] = useState(() => dataPadrao(mostrado));
  const [ferias, setFerias] = useState<ReadonlySet<string>>(new Set());
  const [diarias, setDiarias] = useState<ReadonlySet<string>>(new Set());
  const [confirmando, setConfirmando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const copiar = useCopiarSemana();

  const dataValida = dataSchema.safeParse(data).success;
  const semanas = dataValida ? semanasDaCopia(data, primeiroDia) : null;
  const recorteDaOrigem = {
    residenciaId,
    de: semanas?.origem.de ?? '',
    ate: semanas?.origem.ate ?? '',
  };
  const ausencias = useAusenciasDoPeriodo(recorteDaOrigem, semanas !== null).data ?? [];
  const visitas = useVisitasDoPeriodo(recorteDaOrigem, semanas !== null).data ?? [];

  const feriasMarcadas = ausencias.filter((a) => ferias.has(a.id));
  const diariasMarcadas = visitas.filter((v) => diarias.has(v.id));
  const naoMarcadas = [
    ...ausencias.filter((a) => !ferias.has(a.id)).map((a) => nomeNaEscala(a.colaborador)),
    ...visitas.filter((v) => !diarias.has(v.id)).map((v) => v.visitante),
  ];

  const deAte = (intervalo: { de: string; ate: string }) =>
    `${diaEMes(intervalo.de)} a ${diaEMes(intervalo.ate)}`;

  /** Marca ou desmarca um período, guardando o conjunto novo. */
  function alternar(
    conjunto: ReadonlySet<string>,
    guardar: (novo: ReadonlySet<string>) => void,
    id: string,
    marcado: boolean,
  ) {
    const novo = new Set(conjunto);
    if (marcado) novo.add(id);
    else novo.delete(id);
    guardar(novo);
  }

  /** Passa à confirmação, ou avisa que falta a data. */
  function handleContinuar() {
    setAviso(null);
    if (semanas === null) {
      setAviso('Escolha uma data para definir a semana que vai receber a cópia.');
      return;
    }
    setConfirmando(true);
  }

  /** Grava a cópia e fecha a caixa, ou mostra o que a API recusou. */
  async function handleCopiar() {
    if (semanas === null) return;
    setConfirmando(false);
    try {
      await copiar.mutateAsync({
        residenciaId,
        origem: semanas.origem.de,
        destino: semanas.destino.de,
        ausencias: feriasMarcadas.map((a) => a.id),
        visitas: diariasMarcadas.map((v) => v.id),
      });
      onConcluida();
    } catch (erro) {
      setAviso(
        erro instanceof ErroDaApi
          ? erro.message
          : 'A cópia falhou antes de chegar ao servidor.',
      );
    }
  }

  return (
    <>
      <div className="mb-2.5">
        <Label htmlFor="copia-data" className="mb-2 block">
          Escolha uma data dentro da semana que vai receber a cópia
        </Label>
        <CampoMascarado
          id="copia-data"
          formato="data"
          value={data}
          onChange={setData}
          disabled={copiar.isPending}
        />
      </div>

      {semanas !== null && (
        <p className="mb-4 text-[12.5px] leading-normal text-texto-medio">
          Vai copiar a escala de {deAte(semanas.origem)} (semana anterior) para{' '}
          {deAte(semanas.destino)} (semana escolhida). Os registros atuais dessa semana
          escolhida serão substituídos.
        </p>
      )}

      {(ausencias.length > 0 || visitas.length > 0) && (
        <div className="mb-4 border-t border-linha pt-3">
          <p className="mb-2 text-[11px] font-bold tracking-[0.04em] text-texto-fraco uppercase">
            Esta semana também tem:
          </p>
          {ausencias.length > 0 && (
            <>
              <p className={TITULO_DO_GRUPO}>
                Férias — marque para também copiar (desloca 7 dias) pra semana escolhida:
              </p>
              {ausencias.map((ausencia) => (
                <label key={ausencia.id} className={ITEM_MARCAVEL}>
                  <Checkbox
                    className="mt-0.5"
                    checked={ferias.has(ausencia.id)}
                    onCheckedChange={(marcado) =>
                      alternar(ferias, setFerias, ausencia.id, marcado === true)
                    }
                  />
                  <span>{comoSeLe(nomeNaEscala(ausencia.colaborador), ausencia)}</span>
                </label>
              ))}
            </>
          )}
          {visitas.length > 0 && (
            <>
              <p className={`${TITULO_DO_GRUPO} ${ausencias.length > 0 ? 'mt-2.5' : ''}`}>
                Diárias de prestadores — marque para também copiar (desloca 7 dias):
              </p>
              {visitas.map((visita) => (
                <label key={visita.id} className={ITEM_MARCAVEL}>
                  <Checkbox
                    className="mt-0.5"
                    checked={diarias.has(visita.id)}
                    onCheckedChange={(marcado) =>
                      alternar(diarias, setDiarias, visita.id, marcado === true)
                    }
                  />
                  <span>{comoSeLe(visita.visitante, visita)}</span>
                </label>
              ))}
            </>
          )}
        </div>
      )}

      {aviso !== null && (
        <p role="alert" className="mb-3 text-sm text-perigo">
          {aviso}
        </p>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onConcluida}>
          Cancelar
        </Button>
        <Button type="button" onClick={handleContinuar} disabled={copiar.isPending}>
          {copiar.isPending ? 'Copiando…' : 'Continuar'}
        </Button>
      </DialogFooter>

      <AlertDialog open={confirmando} onOpenChange={setConfirmando}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Copiar semana anterior</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="flex flex-col gap-2.5">
                {semanas !== null && (
                  <p>
                    Copiar a escala de {deAte(semanas.origem)} para a semana de{' '}
                    {deAte(semanas.destino)}? Os registros atuais dessa semana serão
                    substituídos.
                  </p>
                )}
                {feriasMarcadas.length > 0 && (
                  <p>
                    Também serão copiadas (deslocadas 7 dias) as férias de:{' '}
                    {feriasMarcadas.map((a) => nomeNaEscala(a.colaborador)).join(', ')}.
                  </p>
                )}
                {diariasMarcadas.length > 0 && (
                  <p>
                    Também serão copiadas (deslocadas 7 dias) as diárias de:{' '}
                    {diariasMarcadas.map((v) => v.visitante).join(', ')}.
                  </p>
                )}
                {naoMarcadas.length > 0 && (
                  <p>
                    NÃO serão copiadas (continuam só nas datas reais): {naoMarcadas.join(', ')}
                    .
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(evento) => {
                evento.preventDefault();
                void handleCopiar();
              }}
            >
              Copiar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
